import React, { useState, useEffect, useRef } from 'react';
import { MathJax } from 'better-react-mathjax';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { renderBlocks } from '../components/renderBlocks';

export default function AutoCarouselQA({ items }) {
  const [current, setCurrent] = useState(0);
  const timeoutRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const clearAutoScroll = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const scheduleNext = () => {
    clearAutoScroll();

    const blocks = items[current]?.answer_blocks || [];
    const totalText = blocks
      .filter(b => b.type === 'text' || b.type === 'latex')
      .map(b => b.value)
      .join('');
    const length = totalText.length;
    const delay = Math.min(12000, 3000 + length * 25); // adaptive timing

    timeoutRef.current = setTimeout(() => {
      setCurrent((prev) => (prev + 1) % items.length);
    }, delay);
  };

  useEffect(() => {
    if (items.length > 1 && !isHovered) scheduleNext();
    return () => clearAutoScroll();
  }, [current, isHovered]);

  const goTo = (index) => {
    clearAutoScroll();
    setCurrent(index);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => goTo((current + 1) % items.length),
    onSwipedRight: () => goTo((current - 1 + items.length) % items.length),
    trackMouse: true
  });

  if (!items || items.length === 0) return <div>No items found</div>;

  const currentItem = items[current];

  return (
    <div
      className="max-w-4xl mx-auto px-4 py-8"
      {...swipeHandlers}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative overflow-hidden group">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white border border-indigo-200 rounded-xl p-6 shadow-md"
          >
        <div className="text-lg font-bold text-indigo-700 mb-3 flex items-start gap-2">
          <span>Q{currentItem.order_index}:</span>
          <div className="flex-1">
            {Array.isArray(currentItem.question_blocks)
              ? renderBlocks(currentItem.question_blocks)
              : <ReactMarkdown remarkPlugins={[remarkGfm]}>{currentItem.question}</ReactMarkdown>}
          </div>
        </div>


            <div className="text-gray-700 text-base bg-indigo-50 p-4 rounded shadow-inner">
              <p className="font-medium mb-2">Answer:</p>
              {currentItem.answer_blocks
                ? renderBlocks(currentItem.answer_blocks)
                : <MathJax dynamic>{currentItem.answer}</MathJax>
              }
            </div>

            {currentItem.hint && (
              <p className="mt-3 text-sm italic text-indigo-500">
                💡 <strong>Hint:</strong> {currentItem.hint}
              </p>
            )}

            {currentItem.image_url && (
              <div className="mt-4">
                <img
                  src={currentItem.image_url}
                  alt="Visual"
                  className="max-w-full rounded border shadow"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Arrows */}
        <button
          onClick={() => goTo((current - 1 + items.length) % items.length)}
          className="absolute top-1/2 left-2 -translate-y-1/2 p-2 bg-indigo-100 text-indigo-700 rounded-full shadow-lg opacity-0 group-hover:opacity-70 hover:opacity-100 transition"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={() => goTo((current + 1) % items.length)}
          className="absolute top-1/2 right-2 -translate-y-1/2 p-2 bg-indigo-100 text-indigo-700 rounded-full shadow-lg opacity-0 group-hover:opacity-70 hover:opacity-100 transition"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Dots */}
      <div className="mt-4 flex justify-center gap-2">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-3 h-3 rounded-full ${current === index ? 'bg-indigo-600' : 'bg-gray-300'} transition-all duration-300`}
          />
        ))}
      </div>
    </div>
  );
}
